import { v4 as uuidv4 } from 'uuid';
import { getCategoryIdByDescription } from "./categoryMatcher";

// Pass categories directly to parser
export const parseCSVData = (csvData, bank, currentCategories) => {
   const lines = csvData.split('\n');
   let parsed = [];

   for (const line of lines) {
      if (line.trim() === '') continue; // Skip empty lines
      if(isHeader(line)) continue // Skip header row

      // CSV split
      let values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(val => val.replace(/^"|"$/g, '')) || [];
      if (!values || values.length < 5) {
         const simpleValues = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
         if (simpleValues.length >= 5) {
            values = simpleValues;
         } else {
            console.warn("Skipping malformed line:", line);
            continue;
         }
      }

      if (bank === 'wells_fargo') {
         if (values.length >= 5) {
            const transaction = parseWellsFargoData(values, currentCategories);
            // Don't save credits (adjust logic as needed for returns/credits)
            if (transaction && transaction.amount > 0) {
               parsed.push(transaction);
            }
         } else {
            console.warn("Skipping row with unexpected Wells Fargo format:", values);
         }
      } else if(bank === 'chase') {
         const transaction = parseChaseData(values, currentCategories);
         if (transaction && transaction.amount > 0) {
            parsed.push(transaction);
         }
      }
      else {
         console.warn("Unsupported bank type selected for parsing:", bank);
         alert("Unsupported bank type selected."); // Inform user
         return []; // Stop parsing if bank is unsupported
      }
   }
   return parsed;
};

// Parse a CSV line of WF data and return a transaction object
function parseWellsFargoData(data, currentCategories) {
   try {
      // Assuming Wells Fargo format: Date, Amount, ?, ?, Description
      // Indexes might need adjustment based on actual CSV export
      const transactionDateString = data[0];
      const amountString = data[1];
      const description = data[4] || "Other"; // Use index 4 for description

      // Basic validation
      if (!transactionDateString || isNaN(parseFloat(amountString))) {
         console.warn("Skipping row with invalid date or amount:", data);
         return null;
      }

      const epochMs = new Date(transactionDateString).getTime();
      // Check if date parsing was successful
      if (isNaN(epochMs)) {
         console.warn("Skipping row with invalid date format:", transactionDateString);
         return null;
      }

      // Removed timezone offset calculation - backend should handle timezone conversions
      // Send UTC epoch seconds
      const transactionInstant = epochMs / 1000;
      const amount = parseFloat(amountString);

      return {
         id: uuidv4(), // Crucial: DataGrid needs a unique id for each row
         transactionDate: transactionInstant,
         amount: amount * -1, // Invert amount for debits
         category: getCategoryIdByDescription(description, currentCategories),
         description: description,
      };
   } catch (error) {
      console.error("Error parsing Wells Fargo row:", data, error);
      return null; // Return null for rows that cause errors
   }
}

function parseChaseData(data, currentCategories) {
   const transactionDateString = data[0];
   const amountString = data[5];
   const description = data[2] || "Other";

   // Basic validation
   if (!transactionDateString || isNaN(parseFloat(amountString))) {
      console.warn("Skipping row with invalid date or amount:", data);
      return null;
   }

   const epochMs = new Date(transactionDateString).getTime();
   // Check if date parsing was successful
   if (isNaN(epochMs)) {
      console.warn("Skipping row with invalid date format:", transactionDateString);
      return null;
   }
   // Removed timezone offset calculation - backend should handle timezone conversions
   // Send UTC epoch seconds
   const transactionInstant = epochMs / 1000;
   const amount = parseFloat(amountString);

   return {
      id: uuidv4(), // Crucial: DataGrid needs a unique id for each row
      transactionDate: transactionInstant,
      amount: amount * -1, // Invert amount for debits
      category: getCategoryIdByDescription(description, currentCategories),
      description: description,
   };

}

function isHeader(csvLine) {
   let values = csvLine.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(val => val.replace(/^"|"$/g, '')) || [];
   for (const value of values) {
      if(isLikelyDate(value)) {
         return false;
      }
   }
   return true;
}

function isLikelyDate(value) {
   if (typeof value !== 'string' || value.trim() === '') {
      return false;
   }
   const trimmedValue = value.trim();

   const commonDateRegex = /^\d{1,4}[/-]\d{1,2}[/-]\d{1,4}$/;
   if (commonDateRegex.test(trimmedValue)) {
      const dateParts = trimmedValue.split(/[/'-]/);
      if (dateParts.length === 3) {
         const parsedDate = new Date(trimmedValue);
         if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 1990 && parsedDate.getFullYear() < 2100) {
            return true;
         }
      }
   }

   if (!isNaN(parseFloat(trimmedValue)) && isFinite(trimmedValue)) {
      return false;
   }

   return false;
}